import * as React from "react";
import styled, { css } from "styled-components";
import { Motion, spring, presets } from "react-motion";
import debounce from "lodash/debounce";
import range from "lodash/range";

import { ScreenSize } from "../utils/media";
import { media } from "../utils/style";
import Hover, { HoverChildrenFunc } from "../../common/components/Hover";
import Icon from "../../common/components/Icon";
import WithScreenSize, { ScreenSizeProps } from "../../common/components/WithScreenSize";

export interface LayoutParams {
  gutter: number;
  perPage: number;
}

interface OwnProps {
  layouts: { [key in ScreenSize]: LayoutParams };
  childCount: number;
  // TODO(KentoMoriwaki): Use keyword params
  renderChild(index: number, isActive: boolean): React.ReactNode;
}

type Props = OwnProps & ScreenSizeProps;

interface State {
  currentIndex: number;
  /**
   * Current layout should be undefined while initial rendering.
   * Do not use this param for layout, all layouts should be defined by CSS.
   */
  currentLayout?: LayoutParams;
  width?: number;
  moving?: number;
  touchStartTime?: number;
}

class Carousel extends React.Component<Props, State> {
  private outerRef: HTMLElement | null = null;
  private movingStartOffsetX?: number;
  private movingStartOffsetY?: number;
  private wheelAmount?: number;
  private handleResizeDebounced: () => void;
  private stopWheelingDebounced: () => void;

  constructor(props: Props) {
    super(props);

    this.state = {
      currentIndex: 0,
      currentLayout: props.layouts[props.screenSize],
    };

    this.handleResizeDebounced = debounce(this.handleResize, 500);
    this.stopWheelingDebounced = debounce(this.stopWheeling, 50);
  }

  public componentDidMount() {
    window.addEventListener("resize", this.handleResizeDebounced);

    this.setState({
      width: this.getCurrentWidth(),
    });
  }

  public componentWillReceiveProps(nextProps: Props) {
    if (this.props.screenSize !== nextProps.screenSize) {
      this.setState({ currentLayout: nextProps.layouts[nextProps.screenSize] }, () => {
        this.setState({
          width: this.getCurrentWidth(),
        });
      });
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.handleResizeDebounced);
  }

  public handleResize = () => {
    this.setState({
      width: this.getCurrentWidth(),
    });
  };

  public getCurrentWidth() {
    const { gutter, perPage } = this.state.currentLayout!;
    const rect = this.outerRef!.getBoundingClientRect();
    const width = perPage > 1 ? (rect.width - gutter * (perPage - 1)) / perPage : rect.width;
    return Math.round(width * 100) / 100;
  }

  public onTouchStart = (event: React.TouchEvent<HTMLElement>) => {
    this.movingStartOffsetX = event.touches[0].clientX;
    this.movingStartOffsetY = event.touches[0].clientY;
    this.setState({ moving: 0, touchStartTime: new Date().getTime() });
  };

  public onTouchEnd = (_event: React.TouchEvent<HTMLElement>) => {
    const { childCount } = this.props;
    const { width, currentIndex, currentLayout } = this.state;
    const { perPage, gutter } = currentLayout!;
    let { moving } = this.state;
    if (moving === undefined) {
      return;
    }
    const threshold = (width! * perPage + gutter * (perPage - 1)) / 2;
    // Adjust moving by touch speed
    const touchDuration = new Date().getTime() - this.state.touchStartTime!;
    moving += moving / touchDuration * 200; // 200 is empirical number
    // Adjust moving by perPage
    moving *= 1 + (perPage - Math.floor(perPage));
    // Adjust moving by rest child count
    const restCount = moving > 0 ? childCount - currentIndex - perPage : currentIndex;
    if (restCount < perPage) {
      moving *= 1 + (perPage - restCount / perPage);
    }

    let nextIndex = currentIndex;
    if (moving > 0 && moving > threshold) {
      nextIndex += Math.floor(perPage);
    } else if (moving < 0 && -moving > threshold) {
      nextIndex -= Math.floor(perPage);
    }
    this.goToIndex(nextIndex);
  };

  public onTouchMove = (event: React.TouchEvent<HTMLElement>) => {
    const { moving, currentIndex } = this.state;
    if (moving === undefined) {
      return;
    }
    const touch = event.touches[0];
    // If the scroll direction is y-axis, stop moving
    if (
      this.movingStartOffsetY &&
      this.movingStartOffsetX &&
      Math.abs(this.movingStartOffsetY - touch.clientY) > Math.abs(this.movingStartOffsetX - touch.clientX)
    ) {
      this.setState({
        moving: undefined,
      });
      return;
    }
    this.movingStartOffsetY = undefined;
    if (event.cancelable) {
      event.preventDefault();
    }
    const { childCount } = this.props;
    const { perPage } = this.state.currentLayout!;
    const maxIndex = childCount - perPage;
    const minIndex = 0;

    let deltaX;

    if (currentIndex === minIndex && moving < 0) {
      deltaX = (this.movingStartOffsetX! - touch.clientX) / 3; // 3 is empirical number
    } else if (currentIndex === maxIndex && moving > 0) {
      deltaX = (this.movingStartOffsetX! - touch.clientX) / 3; // 3 is empirical number
    } else {
      deltaX = this.movingStartOffsetX! - touch.clientX;
    }

    this.setState({
      moving: deltaX,
    });
  };

  public onWheel = (e: React.WheelEvent<HTMLElement>) => {
    const threshold = 300;
    const { currentIndex, currentLayout } = this.state;
    if (!currentLayout || Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
      return;
    }
    if (e.cancelable) {
      e.preventDefault();
    }
    if (this.wheelAmount === undefined) {
      this.wheelAmount = 0;
    }
    const lastWheelAmount = this.wheelAmount;
    this.wheelAmount += e.deltaX;
    if (e.deltaX > 0 && lastWheelAmount < threshold && this.wheelAmount >= threshold) {
      this.goToIndex(currentIndex + Math.floor(currentLayout.perPage));
    } else if (e.deltaX < 0 && lastWheelAmount > -threshold && this.wheelAmount <= -threshold) {
      this.goToIndex(currentIndex - Math.floor(currentLayout.perPage));
    }
    this.stopWheelingDebounced();
  };

  public stopWheeling = () => {
    this.wheelAmount = undefined;
  };

  public goToIndex(index: number) {
    const { childCount } = this.props;
    const { perPage } = this.state.currentLayout!;
    const maxIndex = childCount - perPage;
    if (index >= maxIndex) {
      index = maxIndex;
    } else if (index < 0) {
      index = 0;
    }
    this.setState({ moving: undefined, touchStartTime: undefined, currentIndex: Math.round(index * 100) / 100 }, () => {
      this.movingStartOffsetX = undefined;
    });
  }

  public onClickNext = () => {
    const { currentIndex, currentLayout } = this.state;
    this.goToIndex(currentIndex + Math.floor(currentLayout!.perPage));
  };

  public onClickPrev = () => {
    const { currentIndex, currentLayout } = this.state;
    this.goToIndex(currentIndex - Math.floor(currentLayout!.perPage));
  };

  public getStyle = () => {
    const { currentLayout } = this.state;
    const { currentIndex, width, moving } = this.state;
    const snap = width && currentLayout ? currentIndex * (width + currentLayout.gutter) : 0;
    return {
      x: moving ? moving + snap : spring(snap, presets.noWobble),
    };
  };

  public setOuterRef = (ref: HTMLElement | null) => {
    this.outerRef = ref;
  };

  public renderNavButtons() {
    const { childCount, screenSize } = this.props;
    const { currentIndex, currentLayout } = this.state;
    if (!currentLayout || screenSize !== "large") {
      return null;
    }
    const maxIndex = childCount - currentLayout.perPage;
    return (
      <>
        {currentIndex > 0 && (
          <CarouselNavPrevWrapper>
            <CarouselNavButton onClick={this.onClickPrev}>
              <ChevronLeftIcon name="wt-icon-chevron-left-o-1" />
            </CarouselNavButton>
          </CarouselNavPrevWrapper>
        )}
        {currentIndex < maxIndex && (
          <CarouselNavNextWrapper>
            <CarouselNavButton onClick={this.onClickNext}>
              <ChevronRightIcon name="wt-icon-chevron-right-o-1" />
            </CarouselNavButton>
          </CarouselNavNextWrapper>
        )}
      </>
    );
  }

  public render() {
    return <Hover>{this.renderWithHover}</Hover>;
  }

  public renderWithHover: HoverChildrenFunc = ({ isHovered, ref }) => {
    const { childCount, layouts, renderChild } = this.props;
    const { width, currentIndex, currentLayout } = this.state;
    if (!currentLayout) {
      return null;
    }
    const { perPage } = currentLayout;
    const activeRange = new Set(range(Math.floor(currentIndex), Math.ceil(currentIndex + perPage)));
    const children = range(0, childCount).map(index => {
      // Do not be active while initial rendering
      const isActive = width ? activeRange.has(index) : true;
      return (
        <Child key={index} layouts={layouts} isActive={isActive}>
          {renderChild(index, isActive)}
        </Child>
      );
    });
    return (
      <Container innerRef={ref}>
        <Motion defaultStyle={{ x: 0 }} style={this.getStyle()}>
          {style => {
            return (
              <Outer
                innerRef={this.setOuterRef}
                onTouchStart={this.onTouchStart}
                onTouchEnd={this.onTouchEnd}
                onTouchMove={this.onTouchMove}
                onWheel={this.onWheel}
              >
                <Inner style={{ transform: `translate3d(${-style.x}px, 0, 0)` }}>{children}</Inner>
              </Outer>
            );
          }}
        </Motion>
        {isHovered && this.renderNavButtons()}
      </Container>
    );
  };
}

const CarouselWithScreenSize: React.SFC<OwnProps> = props => {
  return <WithScreenSize>{({ screenSize }) => <Carousel {...props} screenSize={screenSize} />}</WithScreenSize>;
};

export default CarouselWithScreenSize;

interface LayoutProps {
  isActive: boolean;
  layouts: { [key in ScreenSize]: LayoutParams };
}

const Outer = styled.div`
  position: relative;
`;

const Inner = styled.div`
  position: relative;
  width: 100%;
  display: flex;
`;

const Child = styled.div`
  flex-shrink: 0;
  flex-grow: 0;
  width: ${(p: LayoutProps) =>
    `calc((100% - ${p.layouts.small.gutter}px * ${p.layouts.small.perPage - 1}) / ${p.layouts.small.perPage})`};
  margin-right: ${(p: LayoutProps) => p.layouts.small.gutter}px;
  @media ${media.medium} {
    width: ${(p: LayoutProps) =>
      `calc((100% - ${p.layouts.medium.gutter}px * ${p.layouts.medium.perPage - 1}) / ${p.layouts.medium.perPage})`};
    margin-right: ${(p: LayoutProps) => p.layouts.medium.gutter}px;
  }
  @media ${media.large} {
    width: ${(p: LayoutProps) =>
      `calc((100% - ${p.layouts.large.gutter}px * ${p.layouts.large.perPage - 1}) / ${p.layouts.large.perPage})`};
    margin-right: ${(p: LayoutProps) => p.layouts.large.gutter}px;
  }
  ${(p: { isActive: boolean }) =>
    p.isActive
      ? ""
      : css`
          opacity: 0.3;
        `};
`;

const Container = styled.div`
  position: relative;
`;

const CarouselNavWrapper = styled.div`
  position: absolute;
  top: 0;
  width: 50px;
  height: 100%;
`;

const CarouselNavButton = styled.button`
  position: absolute;
  top: 50%;
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.56);
  border: none;
  font-size: 18px;
  line-height: 44px;
  color: white !important;
  transform: translate3d(0, -100%, 0);
  cursor: pointer;
  outline: none;
  &:hover {
    background-color: rgba(0, 0, 0, 0.56);
  }
`;

const CarouselNavPrevWrapper = styled(CarouselNavWrapper)`
  left: -50px;
`;

const CarouselNavNextWrapper = styled(CarouselNavWrapper)`
  right: -50px;
`;

const ChevronIcon = styled(Icon)`
  font-size: 18px;
`;

const ChevronLeftIcon = ChevronIcon.extend`
  margin-left: -2px;
`;

const ChevronRightIcon = ChevronIcon.extend`
  margin-right: -2px;
`;



// WEBPACK FOOTER //
// ./frontend/src/common/components/Carousel.tsx